import React from 'react';
import { CheckCircle, Eye, Mic, Cpu, Brain, Zap } from 'lucide-react';

const HandsOnProjectsSection = () => {

  const projects = [
    { id: 1, title: "Visual AI Applications", icon: <Eye className="w-8 h-8" />, description: "Build image recognition and computer vision models for real-world applications" },
    { id: 2, title: "Audio Processing & Speech Recognition", icon: <Mic className="w-8 h-8" />, description: "Develop voice assistants and audio analysis systems with advanced AI models" },
    { id: 3, title: "Sensor-Based IoT Projects", icon: <Zap className="w-8 h-8" />, description: "Create smart IoT solutions using sensors and machine learning algorithms" },
    { id: 4, title: "Computer Vision Systems", icon: <Cpu className="w-8 h-8" />, description: "Implement object detection, facial recognition, and image processing systems" },
    { id: 5, title: "Natural Language Processing", icon: <Brain className="w-8 h-8" />, description: "Build chatbots, text analysis tools, and language understanding applications" },
    { id: 6, title: "Robotics & Automation", icon: <CheckCircle className="w-8 h-8" />, description: "Design autonomous robots and automated systems with AI-powered decision making" }
  ];

  // Base styles are kept for clarity
  const styles = {
    container: {
      backgroundColor: '#0a0a0a',
      color: '#ffffff',
      padding: '4rem 2rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '4rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      margin: '0 0 1.5rem 0',
      lineHeight: '1.2'
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#a1a1aa',
      margin: '0',
      maxWidth: '800px',
      marginLeft: 'auto',
      marginRight: 'auto',
      lineHeight: '1.6'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
      marginTop: '3rem'
    },
    card: {
      backgroundColor: '#1a1a1a',
      padding: '2.5rem',
      borderRadius: '20px',
      border: '2px solid #2a2a2a',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      minHeight: '200px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
    },
    backgroundGradient: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      background: 'transparent',
      transition: 'all 0.3s ease',
      borderRadius: '18px',
      opacity: 0
    },
    checkCircleContainer: {
      position: 'absolute',
      top: '1.5rem',
      right: '1.5rem',
      zIndex: 2
    },
    checkCircleIcon: {
      color: '#6366f1',
      fill: 'transparent',
      transition: 'all 0.3s ease'
    },
    mainIconContainer: {
      backgroundColor: '#2a2a2a',
      width: '80px',
      height: '80px',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1.5rem',
      transition: 'all 0.3s ease',
      color: '#6366f1',
      zIndex: 2,
      position: 'relative'
    },
    contentContainer: {
      zIndex: 2,
      position: 'relative'
    },
    cardTitle: {
      fontSize: '1.4rem',
      fontWeight: '600',
      margin: '0 0 1rem 0',
      color: '#ffffff',
      lineHeight: '1.3'
    },
    cardDescription: {
      fontSize: '1rem',
      color: '#a1a1aa',
      margin: '0 auto',
      lineHeight: '1.5',
      maxWidth: '90%',
      maxHeight: '0',
      opacity: '0',
      visibility: 'hidden',
      transition: 'opacity 0.5s ease 0.1s, max-height 0.5s ease 0.1s, visibility 0.5s ease 0.1s'
    },
    borderAnimationContainer: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      borderRadius: '18px',
      background: 'transparent',
      backgroundSize: '200% 200%',
      padding: '2px',
      zIndex: 0
    },
    borderAnimationInner: {
      width: '100%',
      height: '100%',
      backgroundColor: '#1a1a1a',
      borderRadius: '16px'
    }
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          /* Animations */
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          /* Hover Effects */
          [data-component="card"]:hover {
            transform: translateY(-8px) !important;
            border-color: #6366f1 !important;
            box-shadow: 0 20px 40px rgba(99, 102, 241, 0.2) !important;
          }
          [data-component="card"]:hover [data-component="background-gradient"] {
            opacity: 1 !important;
            background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%) !important;
          }
          [data-component="card"]:hover [data-component="check-circle"] svg {
            fill: #6366f1 !important;
          }
          [data-component="card"]:hover [data-component="main-icon"] {
            background-color: #6366f1 !important;
            color: #ffffff !important;
          }
          [data-component="card"]:hover [data-component="description"] {
            max-height: 100px !important; /* Adjust as needed */
            opacity: 1 !important;
            visibility: visible !important;
            animation: fadeIn 0.5s ease-out 0.2s forwards !important;
          }
          [data-component="card"]:hover [data-component="border-animation"] {
            background: linear-gradient(45deg, #6366f1, #8b5cf6, #6366f1) !important;
            animation: gradientShift 2s ease infinite !important;
          }

          /* Responsive Design */
          @media (max-width: 768px) {
            [data-component="grid"] {
              grid-template-columns: 1fr !important;
              gap: 1.5rem !important;
            }
            [data-component="title"] {
              font-size: 2rem !important;
            }
            [data-component="card"] {
              min-height: 180px !important;
              padding: 2rem !important;
            }
          }
          @media (max-width: 480px) {
            [data-component="title"] {
              font-size: 1.75rem !important;
            }
            [data-component="card"] {
              min-height: 160px !important;
              padding: 1.5rem !important;
            }
          }
        `}
      </style>

      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title} data-component="title">
            <span style={{ color: '#6366f1' }}>Hands-On Projects</span> & Prototypes
          </h1>
          <p style={styles.subtitle}>
            Build and train real-world AI models with visual, audio, and sensor-based applications.
          </p>
        </div>

        <div style={styles.grid} data-component="grid">
          {projects.map((project) => (
            <div key={project.id} style={styles.card} data-component="card">
              <div style={styles.backgroundGradient} data-component="background-gradient"></div>

              <div style={styles.checkCircleContainer} data-component="check-circle">
                <CheckCircle className="w-6 h-6" style={styles.checkCircleIcon} />
              </div>

              <div style={styles.mainIconContainer} data-component="main-icon">
                {project.icon}
              </div>

              <div style={styles.contentContainer}>
                <h3 style={styles.cardTitle}>{project.title}</h3>
                <p style={styles.cardDescription} data-component="description">
                  {project.description}
                </p>
              </div>

              <div style={styles.borderAnimationContainer} data-component="border-animation">
                <div style={styles.borderAnimationInner}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HandsOnProjectsSection;